import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, HeartIcon, ShieldCheckIcon, UsersIcon, AwardIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function HeroSection() {
  return <section id="home" className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden min-h-[90vh] flex items-center bg-accent-light" data-id="element-112">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" data-id="element-113">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" data-id="element-114">
          <defs data-id="element-115">
            <pattern id="hero-dots" width="32" height="32" patternUnits="userSpaceOnUse" data-id="element-116">
              <circle cx="2" cy="2" r="1.5" fill="currentColor" className="text-primary" data-id="element-117" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-dots)" data-id="element-118" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10 w-full" data-id="element-119">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-120">
          {/* Left: Text Content */}
          <div className="flex-1 max-w-2xl lg:max-w-none" data-id="element-121">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center gap-2 bg-white text-primary-dark px-4 py-2 rounded-full font-medium text-sm mb-6 shadow-sm border border-accent/20" data-id="element-122">
              <ClockIcon className="w-4 h-4 text-accent" data-id="element-123" />
              <span data-id="element-124">24-48 Hour Onboarding</span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-neutral-dark leading-[1.1] mb-6" data-id="element-125">
              Your Family. <br data-id="element-126" />
              <span className="text-primary" data-id="element-127">Your Caregiver.</span> <br data-id="element-128" />
              Our Support.
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-lg md:text-xl text-neutral-medium mb-8 leading-relaxed max-w-xl" data-id="element-129">
              Want your daughter, son, or nephew to be your caregiver? We
              background check, train, certify, and get them fully approved — so
              your family member becomes a licensed professional caregiver.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-130">
              <Link to="/contact" className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg font-semibold text-center transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-131">
                <HeartIcon className="w-5 h-5" data-id="element-132" />
                Get Started Today
              </Link>
              <Link to="/how-it-works" className="bg-white hover:bg-gray-50 text-primary border-2 border-primary/20 hover:border-primary px-8 py-4 rounded-lg font-semibold text-center transition-all" data-id="element-133">
                Learn How It Works
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }} className="mt-10 flex items-center gap-4 text-sm font-medium text-neutral-medium" data-id="element-134">
              <div className="flex -space-x-2" data-id="element-135">
                {[1, 2, 3, 4].map(i => <img key={i} className="w-8 h-8 rounded-full border-2 border-white" src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="Happy family member" data-id="element-136" />)}
              </div>
              <p data-id="element-137">Trusted by 500+ local families</p>
            </motion.div>
          </div>

          {/* Right: Visual Composition */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="flex-1 relative hidden lg:block" data-id="element-138">
            {/* Main photo */}
            <div className="relative" data-id="element-139">
              <div className="rounded-3xl overflow-hidden shadow-2xl" data-id="element-140">
                <img src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&q=80&w=800&h=600" alt="Professional caregiver smiling with elderly patient at home" className="w-full h-[480px] object-cover" data-id="element-141" />
              </div>

              {/* Decorative ring */}
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full border-2 border-accent/20" data-id="element-142" />
              <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-accent/10" data-id="element-143" />

              {/* Floating stat card — top right */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.7
            }} className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg p-4 flex items-center gap-3 border border-gray-100" data-id="element-144">
                <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-145">
                  <ShieldCheckIcon className="w-5 h-5 text-accent" data-id="element-146" />
                </div>
                <div data-id="element-147">
                  <p className="font-heading font-bold text-neutral-dark text-lg leading-tight" data-id="element-148">
                    RN-Supervised
                  </p>
                  <p className="text-xs text-neutral-medium" data-id="element-149">
                    Clinical Excellence
                  </p>
                </div>
              </motion.div>

              {/* Floating stat card — bottom left */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.9
            }} className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg p-4 flex items-center gap-3 border border-gray-100" data-id="element-150">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-151">
                  <UsersIcon className="w-5 h-5 text-primary" data-id="element-152" />
                </div>
                <div data-id="element-153">
                  <p className="font-heading font-bold text-neutral-dark text-lg leading-tight" data-id="element-154">
                    500+
                  </p>
                  <p className="text-xs text-neutral-medium" data-id="element-155">Families Served</p>
                </div>
              </motion.div>

              {/* Floating stat card — bottom right */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 1.1
            }} className="absolute bottom-16 -right-8 bg-primary rounded-2xl shadow-lg p-4 flex items-center gap-3" data-id="element-156">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-157">
                  <AwardIcon className="w-5 h-5 text-accent" data-id="element-158" />
                </div>
                <div data-id="element-159">
                  <p className="font-heading font-bold text-white text-lg leading-tight" data-id="element-160">
                    Certified
                  </p>
                  <p className="text-xs text-white/70" data-id="element-161">In 24-48 Hours</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}